import { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  InputAdornment,
  alpha,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { colors, shadows } from "../source/source.styles";
import NoDataFound from "../../Reports/NoDataFound";
import SearchIcon from "@mui/icons-material/Search";
import { ArrowLeft, Download } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";

const LiveAnalytics = ({ source, onBack }) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const [searchQuery, setSearchQuery] = useState("");

  // Mock data for hourly lead intake
  const hourlyData = [
    { time: "00:00", leads: 8 },
    { time: "02:00", leads: 6 },
    { time: "04:00", leads: 5 },
    { time: "06:00", leads: 9 },
    { time: "08:00", leads: 12 },
    { time: "10:00", leads: 18 },
    { time: "12:00", leads: 24 },
    { time: "14:00", leads: 28 },
    { time: "16:00", leads: 35 },
    { time: "18:00", leads: 38 },
    { time: "20:00", leads: 42 },
  ];

  // Mock live activity feed data
  const activityFeed = [
    { time: "2 mins ago", leadName: "Rahul Sharma", location: "Mumbai, India", status: "Auto-Assigned", statusColor: "#3B82F6" },
    { time: "5 mins ago", leadName: "Priya Patel", location: "Bangalore, India", status: "In Chat", statusColor: "#10B981" },
    { time: "12 mins ago", leadName: "Amit Verma", location: "Delhi, India", status: "New Lead", statusColor: primaryColor },
    { time: "18 mins ago", leadName: "Sneha Rao", location: "Pune, India", status: "Auto-Assigned", statusColor: "#3B82F6" },
    { time: "24 mins ago", leadName: "Vikram S.", location: "Chennai, India", status: "In Chat", statusColor: "#10B981" },
  ];

  // Filter activity feed based on search query
  const filteredActivity = activityFeed.filter(
    (activity) =>
      activity.leadName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      activity.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
      activity.status.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box sx={{ bgcolor: colors.bgPrimary, minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: "#FFFFFF",
          borderBottom: `1px solid ${colors.border}`,
          p: 3,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton
            onClick={onBack}
            sx={{
              color: colors.textSecondary,
              "&:hover": {
                bgcolor: colors.bgPrimary,
              },
            }}
          >
            <ArrowLeft size={20} />
          </IconButton>

          <Stack spacing={0.5} flex={1}>
            {/* Breadcrumb */}
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography
                component="span"
                onClick={onBack}
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                  cursor: "pointer",
                  "&:hover": { color: colors.primary },
                }}
              >
                REPORTS
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                }}
              >
                ›
              </Typography>
              <Typography
                component="span"
                onClick={onBack}
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                  cursor: "pointer",
                  "&:hover": { color: colors.primary },
                }}
              >
                SOURCES
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                }}
              >
                ›
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: colors.primary,
                  letterSpacing: 0.5,
                }}
              >
                LIVE ANALYTICS
              </Typography>
            </Stack>

            {/* Title */}
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography
                sx={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: colors.textPrimary,
                }}
              >
                Leads by {source?.name || "Facebook"}
              </Typography>
              <Chip
                label="LIVE"
                size="small"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 700,
                  bgcolor: "#FEE2E2",
                  color: "#DC2626",
                  "& .MuiChip-label": {
                    px: 1,
                  },
                }}
              />
            </Stack>
          </Stack>

          <Button
            variant="contained"
            startIcon={<Download size={16} />}
            sx={{
              px: 3,
              py: 1,
              fontSize: 13,
              fontWeight: 600,
              textTransform: "none",
              bgcolor: colors.primary,
              color: "#FFFFFF",
              boxShadow: "none",
              "&:hover": {
                bgcolor: colors.primaryDark,
                boxShadow: "none",
              },
            }}
          >
            Export Live Feed
          </Button>
        </Stack>
      </Box>

      {/* Content */}
      <Box sx={{ p: 4 }}>
        {/* Top Metrics */}
        <Stack direction="row" spacing={3} mb={4}>
          <Box
            sx={{
              flex: 1,
              bgcolor: "#FFFFFF",
              borderRadius: 3,
              p: 3,
              border: `1px solid ${colors.borderLight}`,
              boxShadow: shadows.card,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: colors.textMuted,
                letterSpacing: 0.5,
                mb: 1,
              }}
            >
              LEADS RECEIVED TODAY
            </Typography>
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography
                sx={{
                  fontSize: 32,
                  fontWeight: 700,
                  color: colors.textPrimary,
                }}
              >
                42
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: colors.success,
                }}
              >
                +3%
              </Typography>
            </Stack>
          </Box>

          <Box
            sx={{
              flex: 1,
              bgcolor: "#FFFFFF",
              borderRadius: 3,
              p: 3,
              border: `1px solid ${colors.borderLight}`,
              boxShadow: shadows.card,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: colors.textMuted,
                letterSpacing: 0.5,
                mb: 1,
              }}
            >
              LIVE CONVERSION RATE
            </Typography>
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography
                sx={{
                  fontSize: 32,
                  fontWeight: 700,
                  color: colors.textPrimary,
                }}
              >
                12%
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: colors.textSecondary,
                }}
              >
                Stable
              </Typography>
            </Stack>
          </Box>

          <Box
            sx={{
              flex: 1,
              bgcolor: "#FFFFFF",
              borderRadius: 3,
              p: 3,
              border: `1px solid ${colors.borderLight}`,
              boxShadow: shadows.card,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: colors.textMuted,
                letterSpacing: 0.5,
                mb: 1,
              }}
            >
              AVG. RESPONSE TIME
            </Typography>
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography
                sx={{
                  fontSize: 32,
                  fontWeight: 700,
                  color: colors.textPrimary,
                }}
              >
                1.5m
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: colors.success,
                }}
              >
                Fast
              </Typography>
            </Stack>
          </Box>

          <Box
            sx={{
              flex: 1,
              bgcolor: "#FFFFFF",
              borderRadius: 3,
              p: 3,
              border: `1px solid ${colors.borderLight}`,
              boxShadow: shadows.card,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: colors.textMuted,
                letterSpacing: 0.5,
                mb: 1,
              }}
            >
              TODAY'S AD SPEND
            </Typography>
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography
                sx={{
                  fontSize: 32,
                  fontWeight: 700,
                  color: colors.textPrimary,
                }}
              >
                ₹3,450
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: colors.textSecondary,
                }}
              >
                ₹ Per cap
              </Typography>
            </Stack>
          </Box>
        </Stack>

        {/* Hourly Lead Intake Chart */}
        <Box
          sx={{
            bgcolor: "#FFFFFF",
            borderRadius: 3,
            p: 3,
            border: `1px solid ${colors.borderLight}`,
            boxShadow: shadows.card,
            mb: 4,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
            <Box>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: colors.textPrimary,
                  mb: 0.5,
                }}
              >
                Hourly Lead Intake
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: colors.textSecondary,
                }}
              >
                Real-time lead flow tracking for the current 24-hour period
              </Typography>
            </Box>
            <Button
              variant="text"
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: colors.primary,
                textTransform: "none",
              }}
            >
              Detailed View
            </Button>
          </Stack>

          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={hourlyData}>
              <defs>
                <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={primaryColor} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={primaryColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.borderLight} vertical={false} />
              <XAxis
                dataKey="time"
                tick={{ fontSize: 11, fill: colors.textSecondary }}
                stroke={colors.border}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: colors.textSecondary }}
                stroke={colors.border}
                axisLine={false}
              />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="leads"
                stroke={primaryColor}
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorLeads)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>

        {/* Bottom Section */}
        <Stack direction="row" spacing={3}>
          {/* Live Activity Feed */}
          <Box
            sx={{
              flex: 2,
              bgcolor: "#FFFFFF",
              borderRadius: 3,
              p: 3,
              border: `1px solid ${colors.borderLight}`,
              boxShadow: shadows.card,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: colors.textPrimary,
                }}
              >
                Live Activity Feed
              </Typography>
              <Stack direction="row" alignItems="center" spacing={2}>
                <TextField
                  placeholder="Search activity..."
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: colors.textMuted }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: 200,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      fontSize: 13,
                      bgcolor: colors.bgPrimary,
                      "& fieldset": {
                        borderColor: colors.borderLight,
                      },
                      "&:hover fieldset": {
                        borderColor: colors.border,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: primaryColor,
                        borderWidth: 1,
                      },
                    },
                    "& .MuiOutlinedInput-input": {
                      py: 1,
                    },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: colors.textMuted,
                  }}
                >
                  LAST 10 LEADS
                </Typography>
              </Stack>
            </Stack>

            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      color: colors.textMuted,
                      textTransform: "uppercase",
                      borderBottom: `1px solid ${colors.borderLight}`,
                      py: 1.5,
                    }}
                  >
                    Time
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      color: colors.textMuted,
                      textTransform: "uppercase",
                      borderBottom: `1px solid ${colors.borderLight}`,
                      py: 1.5,
                    }}
                  >
                    Lead Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      color: colors.textMuted,
                      textTransform: "uppercase",
                      borderBottom: `1px solid ${colors.borderLight}`,
                      py: 1.5,
                    }}
                  >
                    Location
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      color: colors.textMuted,
                      textTransform: "uppercase",
                      borderBottom: `1px solid ${colors.borderLight}`,
                      py: 1.5,
                    }}
                  >
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredActivity.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ border: "none" }}>
                      <NoDataFound />
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredActivity.map((activity, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:hover": {
                          bgcolor: colors.bgPrimary,
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: colors.textSecondary,
                          borderBottom: `1px solid ${colors.borderLight}`,
                          py: 2,
                        }}
                      >
                        {activity.time}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: colors.textPrimary,
                          borderBottom: `1px solid ${colors.borderLight}`,
                          py: 2,
                        }}
                      >
                        {activity.leadName}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: colors.textPrimary,
                          borderBottom: `1px solid ${colors.borderLight}`,
                          py: 2,
                        }}
                      >
                        {activity.location}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          borderBottom: `1px solid ${colors.borderLight}`,
                          py: 2,
                        }}
                      >
                        <Chip
                          label={activity.status}
                          size="small"
                          sx={{
                            height: 24,
                            fontSize: 11,
                            fontWeight: 600,
                            bgcolor: `${activity.statusColor}15`,
                            color: activity.statusColor,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>

          {/* Device & Channel Split */}
          <Box
            sx={{
              flex: 1,
              bgcolor: "#FFFFFF",
              borderRadius: 3,
              p: 3,
              border: `1px solid ${colors.borderLight}`,
              boxShadow: shadows.card,
            }}
          >
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                color: colors.textPrimary,
                mb: 3,
              }}
            >
              Device & Channel Split
            </Typography>

            <Stack spacing={4}>
              {/* Device Split */}
              <Box>
                <Stack direction="row" spacing={3} justifyContent="center" mb={2}>
                  <Box sx={{ textAlign: "center" }}>
                    <Box
                      sx={{
                        width: 120,
                        height: 120,
                        borderRadius: "50%",
                        border: `8px solid ${primaryColor}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: 24, fontWeight: 700, color: colors.textPrimary }}>
                        75%
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, letterSpacing: 0.5 }}>
                      IPHONE USERS
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: "center" }}>
                    <Box
                      sx={{
                        width: 120,
                        height: 120,
                        borderRadius: "50%",
                        border: `8px solid ${alpha(primaryColor, 0.4)}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: 24, fontWeight: 700, color: colors.textPrimary }}>
                        60%
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, letterSpacing: 0.5 }}>
                      FACEBOOK VIA MESSENGER
                    </Typography>
                  </Box>
                </Stack>

                {/* Channel breakdown */}
                <Stack spacing={1.5}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: primaryColor,
                        }}
                      />
                      <Typography sx={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary }}>
                        Facebook News Feed
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary }}>
                      60%
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: alpha(primaryColor, 0.4),
                        }}
                      />
                      <Typography sx={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary }}>
                        Messenger Placements
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary }}>
                      40%
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default LiveAnalytics;
